import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { shedService } from '../services/shedService';
import { MODEL_NAMES, MODEL_CATEGORIES, getModelByName, getStartingPrice } from '../constants/shedModels';

const formatPrice = (value) => {
  const n = Number(value);
  if (!n || Number.isNaN(n)) return '$0';
  return `$${n.toLocaleString('en-US')}`;
};

// "Starting at" price for the card — the lowest base price for the model from
// the catalog (Edwin's price list), falling back to the row's own price.
const effectivePrice = (shed) =>
  getStartingPrice(shed.model) ??
  (shed.salePrice && shed.salePrice < shed.price ? shed.salePrice : shed.price);

// Edwin's catalog order, used to lay the model cards out the way he asked.
const MODEL_ORDER = new Map(MODEL_NAMES.map((name, i) => [name, i]));
const modelRank = (shed) =>
  MODEL_ORDER.has(shed.model) ? MODEL_ORDER.get(shed.model) : MODEL_NAMES.length;

const ShedInventory = () => {
  const [sheds, setSheds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const model = searchParams.get('model') || '';
  const category = searchParams.get('category') || '';
  const lot = searchParams.get('lot') || '';
  const minPrice = searchParams.get('minPrice') || '';
  const maxPrice = searchParams.get('maxPrice') || '';
  const sort = searchParams.get('sort') || 'newest';

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const { data } = await shedService.getStorefrontInventory();
      if (active) {
        setSheds(data || []);
        setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  // Lot dropdown options derived from the lots that actually hold inventory.
  const lotOptions = useMemo(
    () => [...new Set(sheds.map((s) => s.lotLocation).filter(Boolean))].sort(),
    [sheds]
  );

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const resetFilters = () => setSearchParams(new URLSearchParams());

  const filteredSheds = useMemo(() => {
    let result = [...sheds];

    if (model) {
      result = result.filter((s) => s.model === model);
    }
    if (category) {
      result = result.filter((s) => getModelByName(s.model)?.category === category);
    }
    if (lot) {
      result = result.filter((s) => s.lotLocation === lot);
    }
    if (minPrice) {
      result = result.filter((s) => effectivePrice(s) >= Number(minPrice));
    }
    if (maxPrice) {
      result = result.filter((s) => effectivePrice(s) <= Number(maxPrice));
    }

    switch (sort) {
      case 'price-low':
        result.sort((a, b) => effectivePrice(a) - effectivePrice(b));
        break;
      case 'price-high':
        result.sort((a, b) => effectivePrice(b) - effectivePrice(a));
        break;
      case 'newest':
      default:
        // Default view: lay the models out in Edwin's catalog order.
        result.sort((a, b) => modelRank(a) - modelRank(b) || effectivePrice(a) - effectivePrice(b));
    }

    return result;
  }, [sheds, model, category, lot, minPrice, maxPrice, sort]);

  const hasFilters = model || category || lot || minPrice || maxPrice;

  const selectClass =
    'border border-secondary-200 rounded-lg px-3 py-2 text-sm text-secondary-900 focus:ring-1 focus:ring-primary-900 focus:border-primary-900 outline-none';

  return (
    <>
      <Helmet>
        <title>Shed Inventory in Dallas-Fort Worth | SE Yard Solutions</title>
        <meta
          name="description"
          content="Browse in-stock sheds on our Fort Worth lot. Filter by model, style, lot and price. Free delivery within 100 miles."
        />
      </Helmet>

      <div className="min-h-screen bg-white pt-16">
        {/* Sticky filter bar */}
        <div className="sticky top-16 z-30 bg-white border-b border-secondary-200">
          <div className="container mx-auto px-4 py-3">
            <div className="flex flex-wrap items-center gap-3">
              {/* Model */}
              <select className={selectClass} value={model} onChange={(e) => setParam('model', e.target.value)}>
                <option value="">All models</option>
                {MODEL_NAMES.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>

              {/* Category */}
              <select className={selectClass} value={category} onChange={(e) => setParam('category', e.target.value)}>
                <option value="">All styles</option>
                {MODEL_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>

              {/* Lot */}
              <select className={selectClass} value={lot} onChange={(e) => setParam('lot', e.target.value)}>
                <option value="">All lots</option>
                {lotOptions.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>

              {/* Price range */}
              <input
                type="number"
                inputMode="numeric"
                placeholder="Min $"
                value={minPrice}
                onChange={(e) => setParam('minPrice', e.target.value)}
                className={`${selectClass} w-24`}
              />
              <input
                type="number"
                inputMode="numeric"
                placeholder="Max $"
                value={maxPrice}
                onChange={(e) => setParam('maxPrice', e.target.value)}
                className={`${selectClass} w-24`}
              />

              {/* Sort */}
              <select className={selectClass} value={sort} onChange={(e) => setParam('sort', e.target.value)}>
                <option value="newest">Featured</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>

              {hasFilters && (
                <button
                  onClick={resetFilters}
                  className="text-sm text-primary-700 hover:text-primary-900 underline"
                >
                  Reset filters
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">In-stock sheds</h1>
          <p className="text-secondary-600 mb-8">
            {loading ? 'Loading inventory...' : `${filteredSheds.length} ${filteredSheds.length === 1 ? 'shed' : 'sheds'} available`}
          </p>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="rounded-lg border border-secondary-200 overflow-hidden animate-pulse">
                  <div className="aspect-[4/3] bg-secondary-100" />
                  <div className="p-4">
                    <div className="h-5 bg-secondary-100 rounded w-2/3 mb-3" />
                    <div className="h-4 bg-secondary-100 rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredSheds.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-lg text-secondary-900 mb-4">No sheds match those filters.</p>
              <button
                onClick={resetFilters}
                className="bg-primary-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-800"
              >
                Reset filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSheds.map((shed) => (
                <Link
                  key={shed.id}
                  to={`/shed/${shed.id}`}
                  className="rounded-lg border border-secondary-200 overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="aspect-[4/3] bg-secondary-100 overflow-hidden">
                    {shed.images?.[0] ? (
                      <img
                        src={shed.images[0]}
                        alt={`${shed.model || 'Shed'} ${shed.size || ''}`}
                        className="w-full h-full object-cover"
                      />
                    ) : null}
                  </div>
                  <div className="p-4">
                    <h2 className="font-semibold text-secondary-900">
                      {shed.model || shed.name}
                    </h2>
                    <p className="mt-1 text-lg font-bold text-primary-900">
                      <span className="text-xs font-medium text-secondary-500 mr-1">From</span>
                      {formatPrice(effectivePrice(shed))}
                      {(getModelByName(shed.model)?.sizes?.length ?? 0) > 1 && (
                        <span className="ml-2 text-xs font-medium text-secondary-500">
                          {getModelByName(shed.model).sizes.length} sizes
                        </span>
                      )}
                    </p>
                    {shed.lotLocation && (
                      <p className="mt-1 text-sm text-secondary-600">{shed.lotLocation}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ShedInventory;
